class AccordionSlider extends HTMLDivElement {
  constructor() {
    super();
    this.init();
    this.initEvents();
  }

  connectedCallback() {
    this.initEvents();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.slideDuration = Number(this.getAttribute('data-slide-duration')) || 5000;
    this.slideAutoplay = this.getAttribute('data-slide-autoplay') === 'true';
    this.slideAutoplayPauseOnInteraction = this.getAttribute('data-slide-autoplay-pause-on-interaction') === 'true';

    this.accordionItems = this.querySelectorAll('[data-slide-accordion]');
    this.mediaItems = this.querySelectorAll('[data-slide-media]');
    this.currentSlideIndex = 0;
    this.isInViewport = false;
    this.observer = null;
    this.startTime = null;
    this.remainingTime = null;
    this.currentTimerDuration = null;
    this.animationEndHandler = null;
    this._hasBeenInViewport = false;

    this.#syncAccessibility(0);
    this.initViewportObserver();
  }

  initEvents() {
    if (this._eventsInitialized) return;
    this._eventsInitialized = true;

    this.accordionClickHandler = this.#handleAccordionClick.bind(this);
    this.mouseEnterHandler = this.#handleMouseEnter.bind(this);
    this.mouseLeaveHandler = this.#handleMouseLeave.bind(this);

    this.addEventListener('click', this.accordionClickHandler);
    this.addEventListener('mouseenter', this.mouseEnterHandler);
    this.addEventListener('mouseleave', this.mouseLeaveHandler);
  }

  cleanup() {
    this._eventsInitialized = false;
    this._hasBeenInViewport = false;
    this.stopAutoplay();
    this.removeEventListener('click', this.accordionClickHandler);
    this.removeEventListener('mouseenter', this.mouseEnterHandler);
    this.removeEventListener('mouseleave', this.mouseLeaveHandler);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  #handleAccordionClick(event) {
    const summary = event.target.closest('summary');
    if (!summary) return;

    const slideItem = summary.closest('[data-slide-accordion]');
    if (!slideItem) return;

    const clickedIndex = Array.from(this.accordionItems).indexOf(slideItem);
    if (clickedIndex === -1) return;

    if (clickedIndex !== this.currentSlideIndex) {
      this.goToSlide(clickedIndex);
    }

    if (this.slideAutoplay) this.restartAutoplay();
  }

  #handleMouseEnter() {
    if (this.slideAutoplay && this.isInViewport && this.slideAutoplayPauseOnInteraction) {
      this.pauseAutoplay();
    }
  }

  #handleMouseLeave() {
    if (this.slideAutoplay && this.isInViewport && this.slideAutoplayPauseOnInteraction) {
      this.resumeAutoplay();
    }
  }

  goToSlide(index) {
    this.currentSlideIndex = index;

    for (const item of [...this.accordionItems, ...this.mediaItems]) {
      item.setAttribute('data-slide-active', 'false');
    }

    if (this.accordionItems[index]) {
      this.accordionItems[index].setAttribute('data-slide-active', 'true');

      if (this.slideAutoplay && !window.matchMedia('(max-width: 809px)').matches) {
        for (const accordion of this.accordionItems) {
          const details = accordion.querySelector('details');
          if (details && details.open && details !== this.accordionItems[index].querySelector('details')) {
            details.open = false;
          }
        }
      }
    }

    if (this.mediaItems[index]) {
      this.mediaItems[index].setAttribute('data-slide-active', 'true');
    }

    this.#syncAccessibility(index);
  }

  #syncAccessibility(index) {
    this.accordionItems.forEach((accordion, i) => {
      const content = accordion.querySelector('.accordion__content');
      if (!content) return;
      const focusableEls = accordion.querySelectorAll('a, button, input, select, textarea');

      if (i === index) {
        content.removeAttribute('aria-hidden');
        focusableEls.forEach((el) => el.removeAttribute('tabindex'));
      } else {
        content.setAttribute('aria-hidden', 'true');
        focusableEls.forEach((el) => el.setAttribute('tabindex', '-1'));
      }
    });

    this.mediaItems.forEach((media, i) => {
      if (i === index) {
        media.removeAttribute('aria-hidden');
      } else {
        media.setAttribute('aria-hidden', 'true');
      }
    });
  }

  nextSlide() {
    const nextIndex = (this.currentSlideIndex + 1) % this.accordionItems.length;
    this.goToSlide(nextIndex);

    const nextDetails = this.accordionItems[nextIndex]?.querySelector('details');
    if (nextDetails) nextDetails.open = true;
  }

  // ─── Progress bar ─────────────────────────────────────────────────────────

  resetProgressBar() {
    for (const accordion of this.accordionItems) {
      accordion.querySelector('details')?.classList.remove('progress-paused', 'progress-running');
    }
  }

  startProgressBar() {
    this.resetProgressBar();

    const activeDetails = this.querySelector('[data-slide-accordion][data-slide-active="true"] details');
    if (!activeDetails) return;

    // Force-restart the ::after animation via the canonical "set to none → reflow → restore" trick.
    // Simply toggling progress-running doesn't restart the animation because the animation-name
    // stays the same and the browser continues from the filled (forwards) state.
    activeDetails.classList.add('progress-reset');
    void activeDetails.offsetHeight;
    activeDetails.classList.remove('progress-reset');
  }

  pauseProgressBar() {
    const activeDetails = this.querySelector('[data-slide-accordion][data-slide-active="true"] details');
    if (!activeDetails) return;
    activeDetails.classList.add('progress-paused');
    activeDetails.classList.remove('progress-running');
  }

  resumeProgressBar() {
    const activeDetails = this.querySelector('[data-slide-accordion][data-slide-active="true"] details');
    if (!activeDetails) return;
    activeDetails.classList.add('progress-running');
    activeDetails.classList.remove('progress-paused');
  }

  // ─── Autoplay ──────────────────────────────────────────────────────────────

  initViewportObserver() {
    if (!window.IntersectionObserver) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isInViewport = entry.isIntersecting;

          if (this.isInViewport) {
            if (!this._hasBeenInViewport) {
              this.goToSlide(0);
              this._hasBeenInViewport = true;
            }

            if (this.slideAutoplay) this.startAutoplay();
          } else {
            this.stopAutoplay();
          }
        });
      },
      { threshold: 0.3 }
    );

    this.observer.observe(this);
  }

  startAutoplay(duration = null) {
    if (this.accordionItems.length <= 1 || !this.slideAutoplay || !this.isInViewport) return;

    this.stopAutoplay();
    this.startProgressBar();
    this.#setupAnimationEndListener();

    this.startTime = Date.now();
    this.currentTimerDuration = duration ?? this.slideDuration;
    this.remainingTime = null;
  }

  stopAutoplay() {
    this.#removeAnimationEndListener();
    this.currentTimerDuration = null;
    this.startTime = null;
    this.remainingTime = null;
  }

  pauseAutoplay() {
    if (this.startTime && this.currentTimerDuration) {
      const elapsed = Date.now() - this.startTime;
      this.remainingTime = Math.max(0, this.currentTimerDuration - elapsed);
    }

    this.#removeAnimationEndListener();
    this.pauseProgressBar();
  }

  resumeAutoplay() {
    this.resumeProgressBar();
    this.#setupAnimationEndListener();

    if (this.remainingTime && this.startTime) {
      this.startTime = Date.now() - (this.currentTimerDuration - this.remainingTime);
      this.remainingTime = null;
    }
  }

  restartAutoplay() {
    if (!this.isInViewport) return;
    this.remainingTime = null;
    this.currentTimerDuration = null;
    this.startAutoplay();
  }

  #setupAnimationEndListener() {
    this.#removeAnimationEndListener();

    const activeDetails = this.querySelector('[data-slide-accordion][data-slide-active="true"] details');
    if (!activeDetails) return;

    this.animationEndHandler = this.#handleAnimationEnd.bind(this);
    activeDetails.addEventListener('animationend', this.animationEndHandler);
  }

  #removeAnimationEndListener() {
    if (!this.animationEndHandler) return;

    for (const details of this.querySelectorAll('[data-slide-accordion] details')) {
      details.removeEventListener('animationend', this.animationEndHandler);
    }

    this.animationEndHandler = null;
  }

  #handleAnimationEnd(event) {
    if (event.animationName !== 'progress-bar') return;
    if (!this.slideAutoplay || !this.isInViewport) return;

    this.nextSlide();
    this.startAutoplay();
  }
}

if (!customElements.get('accordion-slider')) {
  customElements.define('accordion-slider', AccordionSlider, { extends: 'div' });
}
